import { useCallback, useEffect, useState } from 'react';
import { XMLParser } from 'fast-xml-parser';
import { det, prod, XMLDocument } from '../shared/types/nfe';
import * as React from 'react';
import { useForm } from './useForm';
import FornecedorController from '../controllers/FornecedorController';
import ImportacaoXMLController from '../controllers/ImportacaoXMLController';
import { ProdutosResponse } from '../shared/types/importacaoXML';
import { debounce } from '@mui/material';
import { SelectAutoCompleteOptions } from '../shared/types/selectAutoComplete';
import ProdutoController from '../controllers/ProdutoController';
import { toast } from 'react-toastify';
import ClienteController from '../controllers/ClienteController';
import GrupoProdutoController from '../controllers/GrupoProdutoController';
import { Item } from '../shared/components/InputSelect';

const useXmlImport = () => {
  const [fileContent, setFileContent] = useState<XMLDocument | any>({});
  const [activeTab, setActiveTab] = React.useState('1');
  const [loading, setLoading] = React.useState(false);
  const [forncedor, setForncedor] = React.useState<any>(null);
  const [transportador, setTransportador] = React.useState<any>(null);
  const [produtos, setProdutos] = React.useState<ProdutosResponse[]>([]);
  const [produtosSearch, setProdutosSearch] = React.useState<boolean>(false);
  const [modalSearchProduct, setModalSearchProduct] =
    React.useState<boolean>(false);
  const [modalSearchActiveProd, setModalSearchActiveProd] =
    React.useState<prod>();
  const [productOptions, setProductOptions] = React.useState<
    SelectAutoCompleteOptions[]
  >([]);
  const [activeProductSelected, setActiveProductSelected] =
    React.useState<SelectAutoCompleteOptions | null>();
  const [saveXmlIsEnabled, setSaveXmlIsEnabled] =
    React.useState<boolean>(false);
  const [showGroupProduct, setShowGroupProduct] = useState<boolean>(false);
  const [paymentOptions, setPaymentOptions] = useState<Item[]>([]);
  const [productGroup, setGroupProduct] = useState<number>()

  const onSubmit = async (data: any) => {
    setLoading(true);
    setLoading(false);
    return data;
  };

  const form = useForm({
    onSubmit: onSubmit,
    initialValues: fileContent,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = event.target.files?.[0];
    if (file && file.type === 'text/xml') {
      setProdutosSearch(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        const xmlString = e.target?.result as string;
        const parser = new XMLParser({
          numberParseOptions: { leadingZeros: false, hex: true, eNotation: true },
        });
        const jsonResult: any = parser.parse(xmlString);

        if (!jsonResult.nfeProc.NFe.infNFe.det.length) {
          jsonResult.nfeProc.NFe.infNFe.det = [
            jsonResult.nfeProc.NFe.infNFe.det,
          ];
        }
        setFileContent(jsonResult);
      };
      reader.readAsText(file);
    } else {
      alert('Please upload a valid XML file.');
    }
    setLoading(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const getFornecedor = async (cnpj: number | undefined) => {
    if (!cnpj) return;
    console.log('🔎 CNPJ:', cnpj, '| typeof:', typeof cnpj, '| toString:', cnpj?.toString());
    setLoading(true);
    setForncedor(null);
    const response = await FornecedorController.Listar({
      UsuId: 1,
      CpfCnpj: cnpj.toString(),
      Bairro: '',
      Celular: '',
      Cep: '',
      Cidade: '',
      Numero: '',
      Nome: '',
      Endereco: '',
    });
    console.log('📦 Listar →', response.StatusCode, response.Data);
    setLoading(false);
    if (response.StatusCode === 200) {
      return response.Data[0];
    }
  };

  const getTransportador = async (nome: string) => {
    if (!nome) return;
    setLoading(true);
    setTransportador(null);
    const response = await ClienteController.ListarTransportador(nome);

    setLoading(false);
    if (response.StatusCode === 200) {
      return response.Data[0];
    }
  };

  const getProdutos = async (produtos: det[]) => {
    if (produtos?.length === 0) return;

    const codigoBarras = produtos
      .filter((i) => i.prod.cEAN !== 'SEM GTIN')
      .reduce(
        (acc, item) =>
          acc + ` ${item.prod.cEAN}`,
        ''
      );

    return await ImportacaoXMLController.consultarProdutos({
      CodigoBarras: codigoBarras.trim().replace(/ /g, ','),
    });
  };

  // Avisa que a nota ja foi importada ANTES de carregar os dados nas abas.
  // Sem isso o usuario so descobria ao clicar em Salvar, no fim do preenchimento.
  // Retorna true quando a nota ja existe (importacao deve parar).
  const notaJaImportada = async (): Promise<boolean> => {
    const numeroNf = Number(fileContent?.nfeProc?.NFe?.infNFe?.ide?.nNF ?? 0);
    if (!numeroNf) return false;

    const res = await ImportacaoXMLController.verificarNfImportada(numeroNf);
    if (!res?.jaImportada) return false;

    const nota = res?.notas?.[0];
    const detalhe = nota
      ? ` (fornecedor: ${nota.fornecedor ?? 'não identificado'}${nota.nfe_ser ? `, série ${nota.nfe_ser}` : ''})`
      : '';

    toast(`Nota Fiscal ${numeroNf} já importada${detalhe}. Importação cancelada.`, {
      type: 'error',
      autoClose: 6000,
    });
    return true;
  };

  const handleImportRegister = async () => {
    setLoading(true);

    if (await notaJaImportada()) {
      // Limpa o que foi lido do arquivo para nao deixar a tela meio preenchida.
      // Precisa zerar TAMBEM fornecedor/transportador/produtos: sem isso, o estado
      // de um import anterior permanece e a validacao reabilita o botao Salvar XML,
      // que ao ser clicado quebra em handleSaveXML (nfeProc ja foi limpo).
      setFileContent({});
      setForncedor(null);
      setTransportador(null);
      setProdutos([]);
      setSaveXmlIsEnabled(false);
      setLoading(false);
      return;
    }

    const searchFornecedor = getFornecedor(
      fileContent?.nfeProc?.NFe?.infNFe?.emit?.CNPJ
    );
    const searchTransportador = getTransportador(
      fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xNome
    );
    const produtos = await getProdutos(fileContent?.nfeProc?.NFe?.infNFe?.det);

    const [resultFornecedor, resultProdutos, resultTransportador] =
      await Promise.all([searchFornecedor, produtos, searchTransportador]);

    setForncedor(resultFornecedor);
    setProdutos(resultProdutos);
    setTransportador(resultTransportador);
    setLoading(false);
  };

  const updateEmitente = async () => {
    if (!forncedor) return;
    setLoading(true);
    await FornecedorController.Atualizar({
      ...handleEmitente(),
      CliId: forncedor.cli_id,
    });
    setLoading(false);
  };

  const createEmitente = async () => {
    setLoading(true);
    try {
      const response = await FornecedorController.Adicionar(handleEmitente());
      if (response.StatusCode === 204) {
        const result = await getFornecedor(
          fileContent?.nfeProc?.NFe?.infNFe?.emit?.CNPJ
        );
        if (result?.cli_id) {
          setForncedor(result);
        } else {
          // Gravou, mas a re-busca por CNPJ nao localizou para vincular o codigo.
          toast(
            'Fornecedor gravado, mas nao foi localizado para vincular o codigo. Confira o cadastro/CNPJ.',
            { type: 'warning' }
          );
        }
      } else {
        // Antes qualquer status != 204 era engolido em silencio (a tela "nao fazia
        // nada" - sintoma do chamado). Agora a falha aparece para o usuario e fica
        // diagnosticavel (o detalhe do proc sai no log da API quando e 500).
        toast(
          `Nao foi possivel criar o fornecedor (status ${response.StatusCode}).`,
          { type: 'error' }
        );
      }
    } finally {
      // Garante que o spinner sempre para, mesmo se a chamada lancar excecao.
      setLoading(false);
    }
  };

  const updateTransportador = async () => {
    if (!transportador) return;
    setLoading(true);
    await FornecedorController.Atualizar({
      ...handleTransportador(),
      CliId: transportador.cli_id,
    });
    setLoading(false);
  };

  const createTransportador = async () => {
    const payload = handleTransportador();
    if (!payload?.Nome) {
      toast(
        'Nao foi possivel identificar o nome da transportadora no XML. Cadastre manualmente.',
        { type: 'warning' }
      );
      return;
    }
    setLoading(true);
    try {
      const response = await FornecedorController.Adicionar(payload);
      if (response.StatusCode === 204) {
        const result = await getTransportador(
          fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xNome
        );
        if (result?.cli_id) {
          setTransportador(result);
        } else {
          // Gravou mas nao localizou pra vincular o codigo (re-busca e so por nome).
          toast(
            'Transportadora gravada, mas nao foi localizada para vincular o codigo. Confira o cadastro/nome.',
            { type: 'warning' }
          );
        }
      } else {
        toast(
          `Nao foi possivel criar a transportadora (status ${response.StatusCode}).`,
          { type: 'error' }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmitente = () => {
    return {
      CpfCnpj: fileContent?.nfeProc?.NFe?.infNFe?.emit?.CNPJ,
      Nome: fileContent?.nfeProc?.NFe?.infNFe?.emit?.xNome,
      Cidade: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.xMun,
      Cep: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.CEP,
      Bairro: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.xBairro,
      Endereco: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.xLgr,
      Numero: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.nro,
      UsuId: 1,
      RazaoSocial: fileContent?.nfeProc?.NFe?.infNFe?.emit?.xNome,
      Rg_Ie: fileContent?.nfeProc?.NFe?.infNFe?.emit?.IE,
      Telefone: fileContent?.nfeProc?.NFe?.infNFe?.emit?.enderEmit?.fone,
    };
  };

  const handleTransportador = () => {
    return {
      CpfCnpj: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.CNPJ,
      Nome: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xNome,
      Cidade: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xMun,
      Endereco: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xEnder,
      Estado: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.UF,
      UsuId: 1,
      RazaoSocial: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.xNome,
      Rg_Ie: fileContent?.nfeProc?.NFe?.infNFe?.transp?.transporta?.IE,
      IsTransport: true,
    };
  };

  const handleModalSearchProduct = (value: prod) => {
    setModalSearchActiveProd(value);
    setModalSearchProduct(true);
  };

  const handleSearchProduct = async (value: string) => {
    if (!value) return;
    const products = await ProdutoController.Listar({
      Descricao: value,
      Comissao: null,
      ProdutoId_Promatec: '',
      PartNumber: '',
      CodigoBarras: '',
      Tipo: '',
    });

    if (products?.Data?.length) {
      setProductOptions(
        products.Data.map((item: any) => {
          return {
            label: item.prod_descricao,
            value: item.prod_id,
          };
        })
      );
    }
  };

  const handleAutoCompleteSelected = (value: SelectAutoCompleteOptions) => {
    if (!value?.value || !modalSearchActiveProd) return;
    setActiveProductSelected(value);
  };

  const handleCloseModalSearchProduct = () => {
    setModalSearchProduct(false);
    setActiveProductSelected(null);
    setModalSearchActiveProd(undefined);
    setGroupProduct(undefined);
    setShowGroupProduct(false);
  };

  const confirmProductSelected = () => {
    if (!activeProductSelected || !modalSearchActiveProd) return;
    form.setFieldValue(
      `nfeProc.NFe.infNFe.det[${modalSearchActiveProd.id}].prod.prod_id`,
      activeProductSelected.value
    );
    form.setFieldValue(
      `nfeProc.NFe.infNFe.det[${modalSearchActiveProd.id}].prod.prod_found`,
      true
    );
    handleCloseModalSearchProduct();
  };

  const handleSelectGroup = useCallback(async () => {
    const result = await GrupoProdutoController.Listar({} as any);
    setPaymentOptions(
      result.Data.map((item: any) => ({
        value: item.grp_id,
        label: item.grp_siglaid,
      }))
    )
  }, []);

  const handleGroupSelect = (value: any) => {
    setGroupProduct(value.target.value)
  }

  const renderSelectGroup = async () => {
    setShowGroupProduct(true);
  }

  const handleCreateProduct = async () => {
    if (!modalSearchActiveProd) return;
    const barCode =
      modalSearchActiveProd?.cEAN == 'SEM GTIN'
        ? ''
        : modalSearchActiveProd?.cEAN;

    const result = await ProdutoController.Adicionar({
      Descricao: modalSearchActiveProd?.xProd,
      Disponivel: 'S',
      Variacao: '',
      CodigoBarras: barCode,
      UnidadeMedida: modalSearchActiveProd.uCom,
      ValorCusto: modalSearchActiveProd.vProd,
      Marca: '',
      Modelo: '',
      Fabricante: '',
      Observacao: '',
      NCM: modalSearchActiveProd.NCM.toString(),
      UsuId: 1,
      Grupo: productGroup?.toString()
    });

    if (result.StatusCode === 200) {
      await form.setFieldValue(
        `nfeProc.NFe.infNFe.det[${modalSearchActiveProd.id}].prod.prod_id`,
        result.Data.id
      );
      await form.setFieldValue(
        `nfeProc.NFe.infNFe.det[${modalSearchActiveProd.id}].prod.prod_found`,
        true
      );
      toast('Produto cadastrado com sucesso!', {
        type: 'success',
      });
      handleCloseModalSearchProduct();
    }
  };

  const handleSaveXML = async () => {
    const jsonData = form.values?.nfeProc;
    // Guarda: sem nota carregada (ex.: apos uma nota ja importada limpar a tela),
    // nao ha o que salvar. Antes isto estourava em jsonData.NFe e travava o spinner.
    if (!jsonData?.NFe?.infNFe) {
      toast('Nenhuma nota carregada para salvar. Importe um XML valido.', {
        type: 'error',
      });
      return;
    }
    if (!forncedor?.cli_id) {
      toast('Fornecedor nao identificado. Localize ou crie o fornecedor antes de salvar.', {
        type: 'error',
      });
      return;
    }
    setLoading(true);
    try {
      jsonData.NFe.infNFe.emit.for_id = forncedor.cli_id;
      if (transportador?.cli_id) {
        jsonData.NFe.infNFe.transp.transporta.for_id = transportador?.cli_id;
      }
      const result = await ImportacaoXMLController.salvarXml(jsonData);
      if (result.success) {
        toast('XML cadastrada com sucesso!', {
          type: 'success',
          onClose: () => window.location.reload(),
        });
        return;
      }
      toast(result.message, {
        type: 'error',
      });
    } finally {
      // Garante que o spinner sempre para, mesmo se salvarXml lancar excecao.
      setLoading(false);
    }
  };

  const validateSaveXml = () => {
    const infNFe = form?.values?.nfeProc?.NFe?.infNFe;
    // Sem nota carregada, o botao NAO pode habilitar (senao, apos limpar uma nota
    // ja importada, o fornecedor remanescente reabilitava o Salvar indevidamente).
    const hasNota = !!infNFe;
    const productWithoutId = infNFe?.det?.find(
      (item: det) => {
        return !item?.prod?.prod_id;
      }
    );
    const hasForncedor = !!forncedor?.cli_id;

    // Transportadora: o codigo so e obrigatorio quando HA transportadora informada
    // no XML E o frete NAO for "9 - sem ocorrencia de transporte".
    const modFrete = infNFe?.transp?.modFrete?.toString();
    const transporta = infNFe?.transp?.transporta;
    const temTransportadoraInfo = !!(
      transporta?.CNPJ || transporta?.CPF || transporta?.xNome
    );
    const hasTransportador =
      modFrete === '9' || !temTransportadoraInfo || !!transportador?.cli_id;

    setSaveXmlIsEnabled(hasNota && hasForncedor && !productWithoutId && hasTransportador);
  };

  useEffect(() => {
    if (fileContent?.nfeProc && !produtosSearch) {
      setProdutosSearch(true);
      handleImportRegister();
    }
  }, [fileContent]);

  useEffect(() => {
    validateSaveXml();
  }, [form?.values?.nfeProc, forncedor, transportador]);

  useEffect(() => {
    if (produtos.length) {
      const prods = fileContent?.nfeProc?.NFe?.infNFe?.det.map(
        (detItem: det) => ({
          ...detItem,
          prod: {
            ...detItem.prod,
            prod_id:
              produtos.find(
                (item) => item.prod_codigo_barras == detItem.prod.cEAN
              )?.prod_id ?? '',
            prod_found: !!produtos.find(
              (item) => item.prod_codigo_barras == detItem.prod.cEAN
            )?.prod_id,
          },
        })
      );
      setFileContent((previous: XMLDocument) => ({
        ...previous,
        nfeProc: {
          ...previous.nfeProc,
          NFe: {
            ...previous.nfeProc.NFe,
            infNFe: {
              ...previous.nfeProc.NFe.infNFe,
              det: prods,
            },
          },
        },
      }));
    }
  }, [produtos]);

  useEffect(() => {
    handleSelectGroup();
  }, [])

  const debouncedSearchProduct = debounce(handleSearchProduct, 400);

  return {
    handleFileChange,
    fileContent,
    activeTab,
    handleTabChange,
    loading,
    ...form,
    forncedor,
    updateEmitente,
    createEmitente,
    createTransportador,
    updateTransportador,
    transportador,
    produtos,
    handleCloseModalSearchProduct,
    modalSearchProduct,
    handleModalSearchProduct,
    modalSearchActiveProd,
    handleSearchProduct: debouncedSearchProduct,
    productOptions,
    handleAutoCompleteSelected,
    confirmProductSelected,
    activeProductSelected,
    handleCreateProduct,
    handleSaveXML,
    saveXmlIsEnabled,
    renderSelectGroup,
    showGroupProduct,
    paymentOptions,
    handleGroupSelect,
    productGroup,
  };
};

export default useXmlImport;
